// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/mission-execution/tabs/DecisionSupportTab.jsx

import React from "react";
import ExecutionSection from "../shared/ExecutionSection";
import ExecutionContextCard from "../shared/ExecutionContextCard";
import ExecutionActionRow from "../shared/ExecutionActionRow";
import ExecutionEmptyState from "../shared/ExecutionEmptyState";

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const text = value.map((item) => displayValue(item, "")).filter(Boolean).join(", ");
    return text || fallback;
  }
  if (typeof value === "object") {
    const candidate = value.label || value.title || value.name || value.value || value.status || value.state || value.id;
    if (candidate !== undefined && candidate !== null && candidate !== value) return displayValue(candidate, fallback);
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }
  return String(value);
}

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function humanizeDisplayValue(value, fallback = "—") {
  const raw = displayValue(value, "").trim();
  if (!raw) return fallback;

  const normalized = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();

  const exactLabels = {
    soc2: "SOC2",
    "soc 2": "SOC2",
    nist: "NIST",
    iso: "ISO",
    hipaa: "HIPAA",
    ccpa: "CCPA",
    aml: "AML",
    kyc: "KYC",
    fda: "FDA",
    osha: "OSHA",
    sop: "SOP",
    aws: "AWS",
    s3: "S3",
    iam: "IAM",
    waf: "WAF",
    api: "API",
    cloudflare: "Cloudflare",
    okta: "Okta",
    "google workspace": "Google Workspace",
    "risk queue": "Risk Queue",
    "risk queue default": "Risk Queue Default",
    "validation rerun": "Validation Rerun",
    "validation evidence link": "Validation Evidence",
    "remediation task evidence": "Remediation Evidence",
    "archive record": "Archive Record",
    "task scoped": "Task Scoped",
    "not linked": "Not Linked",
    "not loaded": "Not Loaded",
    "in progress": "In Progress",
    "ready for validation": "Ready for Validation",
    "ready to work": "Ready to Work",
    "awaiting validation": "Awaiting Validation",
    "awaiting approval": "Awaiting Approval",
    queued: "Queued",
    failed: "Failed",
    medium: "Medium",
    high: "High",
    critical: "Critical",
    low: "Low",
  };

  if (exactLabels[lower]) return exactLabels[lower];

  return normalized
    .split(" ")
    .map((word) => {
      const key = word.toLowerCase();
      if (exactLabels[key]) return exactLabels[key];
      if (/^cc\d+(?:\.\d+)?$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function humanizeDecisionText(value, fallback = "—") {
  const raw = clean(displayValue(value, ""));
  if (!raw) return fallback;

  return raw.replace(/\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g, (match) => humanizeDisplayValue(match, match));
}

function formatConfidence(decision = {}) {
  const confidence = decision.confidence_score ?? decision.confidence?.confidence_score;
  if (confidence === null || confidence === undefined || confidence === "") return "Confidence —";

  const parsed = Number(confidence);
  if (Number.isFinite(parsed)) return `Confidence ${Math.round(parsed)}%`;

  return `Confidence ${displayValue(confidence)}`;
}

function decisionTaskLabel(task = {}, decision = {}) {
  return (
    clean(task.title) ||
    clean(task.summary) ||
    clean(task.name) ||
    clean(decision.affected_task_title) ||
    clean(decision.task_title) ||
    clean(task.rule_id || task.finding_id) ||
    clean(decision.affected_task) ||
    clean(task.task_id || task.id) ||
    "Selected task"
  );
}

function feedbackActions(decision, onFeedback, decisionLoading) {
  return [
    {
      key: "refresh",
      label: decisionLoading ? "Refreshing…" : "Refresh Decision Support",
      disabled: decisionLoading,
    },
    { key: "useful", label: "Mark Useful", onClick: () => onFeedback?.(decision, "useful") },
    { key: "accept", label: "Accept Advisory", onClick: () => onFeedback?.(decision, "accepted") },
    { key: "reject", label: "Reject", onClick: () => onFeedback?.(decision, "rejected") },
  ];
}

const guidanceBlockStyle = {
  border: "1px solid rgba(96, 165, 250, 0.24)",
  borderRadius: 16,
  padding: 16,
  background: "rgba(15, 23, 42, 0.38)",
  display: "grid",
  gap: 8,
};

const guidanceLabelStyle = {
  color: "#93c5fd",
  fontSize: 12,
  fontWeight: 900,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const guidanceTextStyle = {
  color: "#eaf0ff",
  fontSize: 14,
  lineHeight: 1.55,
  margin: 0,
};

export default function DecisionSupportTab({ task = {}, decision, onRefreshDecision, onFeedback, decisionLoading = false }) {
  if (!decision) {
    return (
      <ExecutionSection
        title="Decision Support"
        description="Week 37 advisory context for this selected task."
        badge="No Decision Loaded"
        badgeTone="neutral"
      >
        <ExecutionEmptyState
          title="No decision support record is loaded for this task yet"
          message="Refresh Decision Support or open the task from a recommendation to view advisory context."
        />
        <ExecutionActionRow
          actions={[
            {
              key: "refresh",
              label: decisionLoading ? "Refreshing…" : "Refresh Decision Support",
              onClick: onRefreshDecision,
              disabled: decisionLoading,
              variant: "primary",
            },
          ]}
        />
      </ExecutionSection>
    );
  }

  const recommendation = displayValue(decision.recommendation, "Decision Support Recommendation");
  const confidenceLabel = formatConfidence(decision);
  const priorityLabel = humanizeDisplayValue(decision.priority, "Priority Unknown");
  const operatorNextStep = humanizeDecisionText(decision.operator_next_step, "Review the selected task context before taking action.");
  const whyNow = humanizeDecisionText(decision.why_now || decision.short_reason || decision.reason, "Decision Support identified this as the next advisory action.");

  return (
    <ExecutionSection
      title="Decision Support"
      description="Advisory recommendation, supporting signals, and operator feedback. No autonomous execution occurs here."
      badge={humanizeDisplayValue(decision.confidence_label || decision.decision_type, "Advisory")}
      badgeTone="default"
    >
      <ExecutionContextCard
        title={recommendation}
        chips={[
          { label: confidenceLabel, tone: "ok" },
          { label: priorityLabel, tone: "warn" },
        ]}
        meta={[
          { label: "Task", value: decisionTaskLabel(task, decision), wrap: true },
          { label: "Evidence Target", value: humanizeDisplayValue(decision.evidence_target, "—"), wrap: true },
          { label: "Validation Blocker", value: humanizeDecisionText(decision.validation_blocker, "—"), wrap: true },
          { label: "Workflow Issue", value: humanizeDecisionText(decision.workflow_transition_issue, "—"), wrap: true },
          { label: "Risk", value: humanizeDecisionText(decision.risk_summary, "—"), wrap: true },
        ]}
        context={whyNow}
      />

      <div style={guidanceBlockStyle}>
        <span style={guidanceLabelStyle}>Recommended Operator Path</span>
        <p style={guidanceTextStyle}>{operatorNextStep}</p>
        <p style={{ ...guidanceTextStyle, color: "#bfdbfe", fontSize: 13 }}>
          Advisory only. OASYS records feedback here but does not execute workflow movement, validation, closure, assignment, or evidence approval automatically.
        </p>
      </div>

      <ExecutionActionRow
        actions={feedbackActions(decision, onFeedback, decisionLoading).map((action) => ({
          ...action,
          onClick: action.key === "refresh" ? onRefreshDecision : action.onClick,
          variant: action.key === "refresh" ? "primary" : action.variant,
        }))}
      />
    </ExecutionSection>
  );
}
